import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkle, User, Lock, ShieldCheck, Pencil, Crown, Instagram, Mail, ChevronDown } from "lucide-react";
import Brand, { BrandLockup } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { http, API } from "@/lib/api";

// ---------------------------------------------------------------------------
// Curated featured work — exact order per brand spec
// Slot 5 (sunflower) is intentionally a placeholder until artwork is supplied.
// ---------------------------------------------------------------------------
const CURATED_WORK = [
  { id: "jesus-lion",       src: "/work/jesus-lion.jpg",       caption: "King of Kings — healed" },
  { id: "native-portrait",  src: "/work/native-portrait.jpg",  caption: "Native portrait" },
  { id: "roaring-bear",     src: "/work/roaring-bear.jpg",     caption: "Roaring bear" },
  { id: "realistic-eye",    src: "/work/realistic-eye.jpg",    caption: "Realistic eye" },
  { id: "sunflower",        src: "/work/sunflower.jpg",        caption: "Sunflower",        healed: true },
  { id: "rose-script",      src: "/work/rose-script.jpg",      caption: "Rose & script",    healed: true },
];

const INSTAGRAM_URL = "https://www.instagram.com/paid_dewz_tattoos";
const CONTACT_EMAIL = "paiddewztattoo@yahoo.com";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    http.get("/public/landing")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Use admin-uploaded gallery if it exists; otherwise the curated default.
  const galleryItems = data?.gallery && data.gallery.length > 0
    ? data.gallery.slice(0, 6).map((g, i) => ({
        id: g.id || `g-${i}`,
        src: g.file_id ? `${API}/public/files/${g.file_id}` : null,
        caption: g.caption || "",
      }))
    : CURATED_WORK;

  const instagramUrl = data?.instagram_url || INSTAGRAM_URL;
  const contactEmail = data?.contact_email || CONTACT_EMAIL;
  // Hero image: admin-uploaded hero takes precedence; fall back to the curated
  // Jesus/Lion piece when nothing has been published.
  const heroSrc = data?.hero_file_id
    ? `${API}/public/files/${data.hero_file_id}`
    : "/work/jesus-lion.jpg";
  const faqs = data?.faqs || [];

  return (
    <div className="min-h-screen">
      <TopNav user={user} />

      {/* HERO — full-bleed signature tattoo with overlay text + CTAs */}
      <section id="top" className="relative">
        <div className="relative h-[88vh] min-h-[640px] w-full overflow-hidden">
          {/* Background image — admin-controlled hero with curated fallback */}
          <img
            src={heroSrc}
            alt="PAID DEWZ Tattoo Studio · Featured work"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "50% 30%" }}
          />
          {/* Cinematic gradient overlay — keeps text legible + ties bottom into page bg */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background:
              "linear-gradient(180deg, rgba(4,6,10,0.55) 0%, rgba(4,6,10,0.30) 35%, rgba(4,6,10,0.65) 75%, rgba(4,6,10,1) 100%)"
          }} />
          {/* Side fade — pulls focus to the centre/left content */}
          <div className="absolute inset-0 pointer-events-none lg:block hidden" style={{
            background: "linear-gradient(90deg, rgba(4,6,10,0.70) 0%, rgba(4,6,10,0.10) 50%, rgba(4,6,10,0) 100%)"
          }} />

          {/* Foreground content */}
          <div className="relative h-full flex items-center">
            <div className="max-w-7xl mx-auto w-full px-6 lg:px-12">
              <div className="max-w-xl stagger-child" style={{ animationDelay: "100ms" }}>
                <span className="overline text-[#D2A853]">Black &amp; Grey Realism</span>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#E8E9E3] mt-5 leading-[0.95] tracking-tight">
                  Custom work,<br />
                  <span className="text-[#D2A853]">considered</span> for one.
                </h1>
                <p className="mt-6 text-base sm:text-lg text-[#C8CFC9] leading-relaxed max-w-md">
                  Private studio · By appointment only. Submit your project, and we&rsquo;ll bring your vision to skin.
                </p>
                <div className="mt-10 flex flex-wrap gap-3">
                  <Link to="/consult" className="btn-brass inline-flex items-center gap-2" data-testid="hero-start-btn">
                    Start Consultation <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/login" className="btn-outline-brass inline-flex items-center gap-2" data-testid="hero-returning-login">
                    Returning Client Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* START HERE — two minimal cards */}
      <section id="start" className="border-b border-[#D2A853]/20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <SectionTitle eyebrow="Begin" title="Start Here" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 mt-12">
            <ChoiceCard to="/consult" icon={User} eyebrow="New" title="New Client"   sub="Start your consultation"  testId="new-client-card" />
            <ChoiceCard to="/login"  icon={Lock} eyebrow="Returning" title="Returning Client" sub="Log in to your account" testId="returning-client-card" />
          </div>
          {data?.availability?.enabled && data.availability.openings?.length > 0 && (
            <AvailabilityNote openings={data.availability.openings} />
          )}
        </div>
      </section>

      {/* FEATURED WORK — exact 3x2 curated grid */}
      <section id="work" className="border-b border-[#D2A853]/20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <SectionTitle eyebrow="Portfolio" title="Featured Work" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6 mt-12" data-testid="featured-gallery">
            {galleryItems.slice(0, 6).map((t, i) => <GalleryTile key={t.id || i} item={t} />)}
          </div>
          <div className="text-center mt-12">
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-brass inline-flex items-center gap-2"
              data-testid="view-more-work"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.6} />
              View More Work
            </a>
          </div>
        </div>
      </section>

      {/* TRUST / AUTHORITY — three blocks */}
      <section className="border-b border-[#D2A853]/15">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 lg:gap-12">
            <TrustBlock icon={ShieldCheck} title="17+ Years Tattooing" sub="Experience. Precision. Consistency." />
            <TrustBlock icon={Pencil}      title="Black & Grey Realism" sub="Custom work. No shortcuts." />
            <TrustBlock icon={Crown}       title="Private Studio"        sub="Appointment only. Focused sessions." />
          </div>
        </div>
      </section>

      {/* THE PROCESS — three simple steps */}
      <section id="process" className="border-b border-[#D2A853]/20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <SectionTitle eyebrow="What to Expect" title="The Process" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12 mt-14">
            <ProcessStep n="01" title="Consultation" body="Submit your request and describe your idea." />
            <ProcessStep n="02" title="Deposit"      body="Secure your appointment." />
            <ProcessStep n="03" title="Appointment"  body="We bring your design to life." />
          </div>
        </div>
      </section>

      {/* FAQ — only renders when admin has published at least one Q/A */}
      {faqs.length > 0 && (
        <section id="faq" className="border-b border-[#D2A853]/20">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
            <SectionTitle eyebrow="Common Questions" title="FAQ" />
            <div className="mt-12 divide-y divide-[#D2A853]/15" data-testid="faq-list">
              {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <Footer instagramUrl={instagramUrl} contactEmail={contactEmail} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function TopNav({ user }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/30 border-b border-[#D2A853]/15">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center" data-testid="nav-brand">
          <BrandLockup size="sm" dataTestId="nav-brand-lockup" />
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {[["Work","#work"],["Process","#process"],["FAQ","#faq"]].map(([label, href]) => (
            <a key={label} href={href} className="overline text-[#A3A8A4] hover:text-[#D2A853] transition" data-testid={`nav-${label.toLowerCase()}`}>{label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="overline text-[#D2A853] hover:text-[#E2BC6A]" data-testid="nav-dashboard">
              Dashboard →
            </Link>
          ) : (
            <Link to="/login" className="hidden sm:inline overline text-[#A3A8A4] hover:text-[#D2A853]" data-testid="nav-login">
              Log In
            </Link>
          )}
          <Link to="/consult" className="btn-brass !px-4 !py-2 !text-xs hidden sm:inline-flex items-center" data-testid="nav-cta">
            Start Consultation
          </Link>
          <button onClick={() => setOpen(!open)} className="md:hidden text-[#D2A853]" data-testid="nav-mobile-toggle" aria-label="menu">
            <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#D2A853]/15 bg-black/40 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-4">
            {[["Work","#work"],["Process","#process"],["FAQ","#faq"]].map(([label, href]) => (
              <a key={label} href={href} onClick={() => setOpen(false)} className="overline text-[#A3A8A4] hover:text-[#D2A853]">{label}</a>
            ))}
            <Link to="/login" className="overline text-[#A3A8A4]">Log In</Link>
            <Link to="/consult" className="btn-brass !text-xs inline-flex items-center justify-center">Start Consultation</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function SectionTitle({ eyebrow, title }) {
  return (
    <div className="text-center">
      <span className="overline text-[#D2A853]">{eyebrow}</span>
      <h2 className="font-display text-4xl sm:text-5xl text-[#E8E9E3] mt-3 tracking-tight">{title}</h2>
      <div className="ornate-rule mt-6 max-w-xs mx-auto">
        <Sparkle className="w-3 h-3" strokeWidth={1.4} />
      </div>
    </div>
  );
}

function ChoiceCard({ to, icon: Icon, eyebrow, title, sub, testId }) {
  return (
    <Link to={to} className="frame-card ornate-frame p-10 group block transition-all hover:bg-[#2A3A30]" data-testid={testId}>
      <div className="flex items-center justify-center w-14 h-14 mx-auto border border-[#D2A853]/40 mb-6 group-hover:border-[#D2A853]">
        <Icon className="w-6 h-6 text-[#D2A853]" strokeWidth={1.4} />
      </div>
      <div className="text-center">
        <span className="overline text-[#D2A853] text-[10px]">{eyebrow}</span>
        <h3 className="font-display text-2xl sm:text-3xl text-[#E8E9E3] mt-3 mb-3">{title}</h3>
        <p className="text-sm text-[#A3A8A4] leading-relaxed max-w-xs mx-auto">{sub}</p>
        <div className="inline-flex items-center gap-2 mt-8 text-[#D2A853] group-hover:text-[#E2BC6A]">
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.4} />
        </div>
      </div>
    </Link>
  );
}

function AvailabilityNote({ openings }) {
  return (
    <div className="mt-12 max-w-2xl mx-auto text-center" data-testid="availability-cta">
      <div className="ornate-rule mb-6"><Sparkle className="w-3 h-3" strokeWidth={1.4} /></div>
      <p className="overline text-[#D2A853]">Limited Openings Available For Approved Projects</p>
      <div className="flex flex-wrap justify-center gap-3 mt-5">
        {openings.map((o, i) => (
          <span key={i} className="overline text-[#D2A853] text-[10px] px-4 py-2 border border-[#D2A853]/40">{o.kind}</span>
        ))}
      </div>
    </div>
  );
}

function GalleryTile({ item }) {
  return (
    <div className="aspect-square frame-card overflow-hidden relative group" data-testid={`work-tile-${item.id}`}>
      {item.src ? (
        <img
          src={item.src}
          alt={item.caption || ""}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1F2A22] text-center px-4">
          <Brand variant="submark" className="h-10 w-10 opacity-30 mb-3" />
          <span className="overline text-[#D2A853]/60 text-[10px]">{item.caption || "Coming soon"}</span>
        </div>
      )}
      {item.healed && item.src && (
        <div
          className="absolute inset-x-0 bottom-0 pt-12 pb-3 text-center pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)" }}
          data-testid={`healed-badge-${item.id}`}
        >
          <span className="overline text-[#D2A853] text-[10px] tracking-[0.3em]">Healed</span>
        </div>
      )}
    </div>
  );
}

function TrustBlock({ icon: Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center text-center" data-testid={`trust-${title.toLowerCase().replace(/\W+/g, '-')}`}>
      <div className="w-12 h-12 flex items-center justify-center border border-[#D2A853]/40 mb-5">
        <Icon className="w-5 h-5 text-[#D2A853]" strokeWidth={1.4} />
      </div>
      <div className="font-display text-xl text-[#E8E9E3] tracking-wide">{title}</div>
      <div className="brass-divider w-12 my-3" />
      <div className="text-sm text-[#A3A8A4] max-w-xs leading-relaxed">{sub}</div>
    </div>
  );
}

function ProcessStep({ n, title, body }) {
  return (
    <div className="text-center" data-testid={`process-${n}`}>
      <div className="font-display text-5xl text-[#D2A853]/40 mb-3">{n}</div>
      <h3 className="font-display text-2xl text-[#E8E9E3] mb-3 tracking-wide">{title}</h3>
      <p className="text-sm text-[#A3A8A4] leading-relaxed max-w-xs mx-auto">{body}</p>
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-6">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 text-left group"
        data-testid={`faq-toggle-${index}`}
        aria-expanded={open}
      >
        <span className="font-display text-base sm:text-lg text-[#E8E9E3] group-hover:text-[#D2A853] transition">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-[#D2A853] transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.4}
        />
      </button>
      {open && (
        <p className="text-sm text-[#A3A8A4] leading-relaxed mt-4 pr-8" data-testid={`faq-answer-${index}`}>
          {a}
        </p>
      )}
    </div>
  );
}

function Footer({ instagramUrl, contactEmail }) {
  return (
    <footer data-testid="footer">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="flex flex-col items-center text-center gap-5">
          <BrandLockup size="md" dataTestId="footer-brand-lockup" />
          <div className="overline text-[#D2A853] text-[10px] mt-1">Black &amp; Grey Realism · Private Studio</div>
          <div className="flex items-center gap-5 mt-2">
            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[#A3A8A4] hover:text-[#D2A853] transition" data-testid="footer-instagram">
                <Instagram className="w-5 h-5" strokeWidth={1.4} />
              </a>
            )}
            {contactEmail && (
              <a href={`mailto:${contactEmail}`} aria-label="Email" className="overline text-[#A3A8A4] hover:text-[#D2A853] inline-flex items-center gap-2 transition" data-testid="footer-email">
                <Mail className="w-4 h-4" strokeWidth={1.4} />
                <span className="hidden sm:inline">{contactEmail}</span>
              </a>
            )}
          </div>
          <Link to="/consult" className="btn-brass inline-flex items-center gap-2 mt-4" data-testid="footer-cta">
            Start Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-12 pt-6 border-t border-[#D2A853]/15 flex flex-col sm:flex-row items-center justify-between gap-3 text-center">
          <span className="overline text-[#A3A8A4]/60 text-[10px]">© {new Date().getFullYear()} PAID DEWZ Tattoo Studio · All work is custom</span>
          <Link to="/aftercare" className="overline text-[#A3A8A4]/60 hover:text-[#D2A853] text-[10px] transition" data-testid="footer-aftercare">
            Tattoo Aftercare
          </Link>
        </div>
      </div>
    </footer>
  );
}
